import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type {
	IDexfSettings,
	ILocalizedInformation,
	IShoppingProduct
} from '@inter-ikea-kompis/types';
import { DexfSettingsUtility, ProductUtility, ShoppingSupport } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';
import type { IKompisElementPropertyValue } from '@inter-ikea-kompis/web-component';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { checkmark, heart, registerIcon, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';

import AddToListLayoutEnum from '../../enums/AddToListLayoutEnum.js';
import AddToListStateEnum from '../../enums/AddToListStateEnum.js';
import AddToListTracker from '../../trackers/AddToListTracker.js';
import type IAddToListAnalyticsProp from '../../types/IAddToListAnalyticsProp.js';
import type IAddToListTranslations from '../../types/IAddToListTranslations.js';
import AddToListStyle from './AddToListStyle.js';

/**
 * View component for adding products to shopping list.
 *
 * @event addToList Start adding products to shopping list.
 * @event resetState Sent after 3 seconds after addToListState confirmation to be able to reset the state of the button.
 *
 * @tracker packages/component-add-to-list/src/trackers/AddToListTracker.ts
 */

export default class AddToList extends KompisElement {
	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IAddToListTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IDexfSettings | null = null;

	// Defines the state for add to list button
	@property({ type: String })
	public state: AddToListStateEnum = AddToListStateEnum.default;

	// List of items including the quantity.
	@property({ type: Object, required: true })
	public shoppingProducts!: IShoppingProduct[];

	// Disables the button.
	@property({ type: Boolean })
	public disabled = false;

	// Layout
	@property({ type: String })
	public layout: AddToListLayoutEnum = AddToListLayoutEnum.icon;

	// Icon variant
	@property({ type: String })
	public iconVariant: ButtonVariant = 'secondary';

	// Used for automatic analytics. If null or not set, analytics is turned off.
	@property({ type: Object })
	public analytics: IAddToListAnalyticsProp | null = null;

	private triggerElement: HTMLElement | null = null;
	private tracker: AddToListTracker | null = null;
	private timeout: ReturnType<typeof setTimeout> | null = null;
	private dexfSettings: IDexfSettings | null = null;

	public override async connectedCallback(): Promise<void> {
		registerIcon(checkmark);
		registerIcon(heart);
		registerIcon(warningTriangle);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);

		super.connectedCallback();
	}

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${AddToListStyle.getStyle({
					addToListLayout: this.layout
				})}
			</style>

			${this.getListButton()}
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.disconnectTracker();

		if (this.timeout) {
			clearTimeout(this.timeout);
		}
	}

	protected override shouldUpdate(
		changedProperties: Map<string, IKompisElementPropertyValue>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}

		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		// Once the add to list-button have been set with the state 'confirmation' there should be an event sent after 3 seconds to make it possible to reset its state to 'default' again.
		if (changedProperties.has('state') && this.state === AddToListStateEnum.confirmation) {
			this.dispatchResetState();
		}

		if (changedProperties.has('analytics') && this.hasTranslationsAndSettings()) {
			this.disconnectTracker();
			this.connectTracker();
		}

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getSettings(): IDexfSettings {
		return <IDexfSettings>this.dexfSettings;
	}

	private getTranslations(): IAddToListTranslations {
		const translations = this.translations
			? this.translations
			: <IAddToListTranslations>this.localizedInformation?.translations;

		return translations;
	}

	private dispatchResetState(): void {
		if (this.timeout) {
			clearTimeout(this.timeout);
		}

		this.timeout = setTimeout(() => {
			this.dispatchEvent(new CustomEvent('resetState', { bubbles: true }));
		}, 3000);
	}

	private async connectTracker(): Promise<void> {
		if (this.analytics) {
			const reporter = this.analytics.__INTERNAL__reporter;
			if (reporter?.connected === false) {
				// Should use an existing reporter, but wait for it to be connected
				return;
			}

			// Use existing connected reporter, or the tracker should create a new one.
			const reporterId = reporter ? reporter.id : undefined;

			this.tracker = new AddToListTracker({
				...this.analytics,
				settings: this.getSettings(),
				reporterId
			});
			await this.tracker?.connect();
		}
	}

	private disconnectTracker(): void {
		this.tracker?.disconnect();
		this.tracker = null;
	}

	private getListButton(): RenderResult {
		if (!ShoppingSupport.hasListSupport(this.getSettings())) {
			return null;
		}

		const { addToShoppingListButtonSuccess, addToShoppingListButton, loading } =
			this.getTranslations();

		const primaryVariant: ButtonVariant = 'primary';
		const success = this.state === AddToListStateEnum.confirmation;
		const label = success ? addToShoppingListButtonSuccess : addToShoppingListButton;

		if (this.layout === AddToListLayoutEnum.text) {
			return html`
				<skapa-button
					?loading="${!this.disabled && this.state === AddToListStateEnum.loading}"
					loadingSrText="${loading}"
					variant="${primaryVariant}"
					?disabled="${this.disabled || !this.hasPositiveQuantity()}"
					@click="${this.onAddToList.bind(this)}"
					fluid
				>
					${label}
				</skapa-button>
			`;
		}

		return html`
			<skapa-icon-button
				size="${this.layout === AddToListLayoutEnum.iconSmall ? 'small' : 'medium'}"
				?loading="${!this.disabled && this.state === AddToListStateEnum.loading}"
				loadingSrText="${loading}"
				variant="${this.iconVariant}"
				?disabled="${this.disabled || !this.hasPositiveQuantity()}"
				@click="${this.onAddToList.bind(this)}"
			>
				${label}
				<skapa-icon slot="icon" icon="${this.getListButtonIcon()}"></skapa-icon>
			</skapa-icon-button>
		`;
	}

	private getListButtonIcon(): 'heart' | 'checkmark' {
		if (this.disabled || !this.hasPositiveQuantity()) {
			return 'heart';
		}

		switch (this.state) {
			case AddToListStateEnum.confirmation:
				return 'checkmark';
			default:
				return 'heart';
		}
	}

	private setTriggerElement(target: EventTarget | null): Element | null {
		if (target instanceof Element) {
			this.triggerElement = this.getTriggerElement();
		}

		return this.triggerElement;
	}

	private getTriggerElement(): HTMLElement {
		return <HTMLElement>(
			(
				this.shadowRoot?.querySelector(this.customElements.getInternalName('skapa-button')) ??
				this.shadowRoot?.querySelector(this.customElements.getInternalName('skapa-icon-button'))
			)?.shadowRoot?.querySelector('button')
		);
	}

	private getValidShoppingProducts(): IShoppingProduct[] {
		return this.shoppingProducts
			.filter((shoppingProduct) => shoppingProduct.quantity > 0)
			.filter((shoppingProduct) => shoppingProduct.product.content);
	}

	private onAddToList(event: MouseEvent | KeyboardEvent): void {
		const shoppingProducts = this.getValidShoppingProducts();
		const shoppingItems = ProductUtility.getShoppingItems(shoppingProducts);
		const triggerElement = this.setTriggerElement(event.currentTarget);

		this.dispatchEvent(
			new CustomEvent('addToList', {
				bubbles: true,
				detail: { shoppingItems, triggerElement }
			})
		);
		this.tracker?.sendAddToListClick();
	}

	private hasPositiveQuantity(): boolean {
		return this.shoppingProducts.some((shoppingProduct) => shoppingProduct.quantity > 0);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
		'kompis-add-to-list': AddToList;
	}
}
